/*
 * @Copyright 2026. Institute for Future Intelligence, Inc.
 *
 * Bead model for the molecular lab: the foundation of its coarse-grained
 * resolutions (united atom, bead-spring, ellipsoid).
 *
 * WHY. Every particle in the molecular lab is an Atom built from a chemical
 * element: the Atom constructor takes an element symbol and reads the mass from
 * the element table and σ / ε from UFF (uffParameters.ts). A coarse-grained
 * particle — a CH₂ united atom, a polymer bead standing for one monomer, a
 * Gay-Berne rod standing for a whole mesogen — is not an element, has no entry
 * in either table, and no SDF record of its own.
 *
 * HOW. A coarse-grained molecule keeps an SDF for what the whole pipeline needs
 * from one — positions, bonds, the atom count, a thumbnail — written with a
 * PLACEHOLDER element per bead (the heavy atom of a united atom, carbon for a
 * generic bead), and carries a BeadModel beside it: the bead types it uses and,
 * parallel to the SDF atom block, which type each atom is. applyBeadModel()
 * then overrides the placeholder's mass / σ / ε / charge on every atom, in the
 * same place and the same way the species force field overrides UFF for argon
 * or water: in the MolecularDynamics constructor (so the lab's mirror, the
 * worker's copy and headless tools agree), on a Molecule clone, and wherever
 * the gallery parses an SDF into engine atoms. The atom remembers its type in
 * Atom.bead, which is what the renderer, the hover tip and the lab chip will
 * read (todo #2) and what the Gay-Berne kernel will read for the aspect ratio
 * (todo #4).
 *
 * UNITS. Every built-in type is in the engine's real units (g/mol, Å, eV),
 * fitted to a reference case (see GENERIC_BEAD_TYPES), so the thermometer and
 * the charts read kelvin, picoseconds and ångströms for every model. A custom
 * type may still be declared in reduced Lennard-Jones units (m = σ = ε = 1)
 * through BeadModel.units = 'reduced' and BeadModel.scale; the charts then
 * recover reduced time as t* = t·√(ε / m σ²) from that scale.
 *
 * BONDS. A bead molecule's bonded terms are the bead model's own, applied by
 * applyBeadModelToMolecule after the MMFF-lite typer has run (beadBondedTerms):
 * every real-unit chain (united-atom or one of the fitted bead types) has the
 * TraPPE flexible bond (452 900 K/Å²) at the built length, the 114° backbone
 * angle (62 500 K/rad²) and the 3-fold term of the OPLS torsion, so a chain of
 * monomer or residue beads is semiflexible rather than freely
 * jointed (the reference fits above were made with these terms). A custom
 * reduced-unit chain has harmonic springs of 800 ε/σ² at the built length (the
 * stiffness of the Kremer–Grest FENE + WCA pair near its 0.97 σ minimum) and no
 * angle or torsion terms. Angles / torsions the lab generated are kept in the
 * lists with their bead strength (zero for a reduced chain), so nothing else
 * has to learn that a molecule may have none. The lab derives those lists from
 * the radial bonds when it loads a molecule (dynamicsViewer); a headless run of
 * a built chain (the example seeders, tests) gets the same lists from
 * deriveBeadTopology, so both run the same semiflexible chain.
 *
 * LIMITS (for now): a rod meets a sphere through a spherical pair at the rod's
 * center, so a sphere can reach inside a rod's tips.
 *
 * Verified by beadModel.test.ts (npm run test:beads).
 */
import { Quaternion, Vector3 } from 'three';
import Element from '../lib/chem/Element';
import type { BeadModel, BeadPairRule, BeadType, MoleculeInterface, ParticleResolution } from '../types.ts';
import { Atom } from './Atom.ts';
import { Molecule } from './Molecule.ts';
import { RadialBond } from './RadialBond.ts';
import { AngularBond } from './AngularBond.ts';
import { TorsionalBond } from './TorsionalBond.ts';
import { ModelUtil } from './ModelUtil.ts';
import { evToKelvin, kelvinToEv } from './speciesForceField.ts';
import { gbPair, gbUnlikeParams, newGbPairResult } from './gayBerne.ts';

/** MoleculeInterface.source of a molecule built from or reduced to beads. */
export const MOLECULE_SOURCE_COARSE_GRAINED = 'Coarse-grained model';

/**
 * The reduced-unit scale a generic bead model maps onto the engine with:
 * σ = 3.4 Å, ε/k = 120 K, m = 40 g/mol (an argon-like bead). One reduced time
 * unit τ = σ·√(m/ε) is then ≈ 2.2 ps.
 */
export const DEFAULT_BEAD_SCALE: NonNullable<BeadModel['scale']> = {
  sigma: 3.4,
  epsilon: kelvinToEv(120),
  mass: 40,
};

// TraPPE-UA alkane groups (Martin & Siepmann, J. Phys. Chem. B 102, 2569
// (1998); CH and C from the branched-alkane paper, ibid. 103, 4508 (1999)).
// Each group is one bead carrying its hydrogens' mass; the placeholder written
// in the SDF is the carbon it is centered on, so the renderer's element color
// and the bonding perception still make sense until todo #2 draws beads.
const ua = (name: string, label: string, mass: number, sigma: number, epsilonOverK: number): BeadType => ({
  name,
  label,
  symbol: 'C',
  mass,
  sigma,
  epsilon: kelvinToEv(epsilonOverK),
});

export const UNITED_ATOM_TYPES: readonly BeadType[] = [
  ua('CH4', 'methane, united atom', 16.043, 3.73, 148),
  ua('CH3', 'methyl group, united atom', 15.035, 3.75, 98),
  ua('CH2', 'methylene group, united atom', 14.027, 3.95, 46),
  ua('CH', 'methine group, united atom', 13.019, 4.68, 10),
  ua('CQ', 'quaternary carbon, united atom', 12.011, 6.4, 0.5),
];

// Generic beads in REAL units, each fitted to a reference case so the lab's
// thermometer reads kelvin for every model (the reduced-unit machinery below
// stays for custom types that declare it).
const generic = (
  name: string,
  label: string,
  color: string,
  mass: number,
  sigma: number,
  epsilonOverK: number,
  extra: Partial<BeadType> = {},
): BeadType => ({
  name,
  label,
  symbol: 'C',
  mass,
  sigma,
  epsilon: kelvinToEv(epsilonOverK),
  color,
  ...extra,
});

// The built-in coarse-grained models, each in real units fitted to a
// reference case (the fits are checked by the calibration runs recorded in
// beadModel.test.ts and gayBerne.test.ts):
//  - B    one polystyrene repeat unit (104 g/mol, 5 Å). Refit 2026-11-01 with csvr:
//         a free 400-bead melt of 20-mers cages (body-frame MSD levels off in a
//         10 ps window) near 280 K at ε/k 830, 430 ± 180 at 1000, 470 at 1150, so
//         930 K, interpolated to polystyrene's 373 K. Scatter is large, and this is
//         a kinetic transition at ~10¹² K/s in a blob that is half surface.
//  - ROD  5CB, the display liquid crystal (249 g/mol, 18 × 5 Å → κ = 3.6); the
//         Gay-Berne well (ε₀/k = 540 K) keeps a 216-rod lattice with 10 Å of
//         room in its box (ρ* 0.16) nematic at 293 K (S ≈ 0.8 over 100 ps) and
//         lets it clear on heating (S 0.64 at 353 K, 0.4 at 413 K, falling);
//         440 K lost its order at 293 K and 640 K held it to 413 K. The walls
//         act on the rods' tips (MolecularDynamics.applyBoundary), so a lattice
//         that fills its box is jammed solid at any temperature — the margin
//         is what lets it melt. Clearing sits well above 5CB's 308 K.
//  - H/P  one amino-acid residue each (110 g/mol, Cα spacing 3.8 Å): H beads
//         attract with a well that folds a hydrophobic–polar chain at room
//         temperature and opens it by ~420 K; any pair with a P bead repels.
//  - HD/TL  a soap-like lipid: Martini-sized tails (four heavy atoms, 72 g/mol,
//         4.7 Å) under a WIDE 7 Å head, so the molecule is a cone and packs into
//         small spherical micelles (see the HD row); the tail attraction is 1.5×
//         Martini's 3.5 kJ/mol because, with no water beads, it also carries the
//         hydrophobic effect (see the TL row); heads repel everything, standing
//         in for the water the model leaves out.
const REPULSIVE: BeadPairRule = { repulsive: true };
// A Cα-trace backbone for residue beads: a soft harmonic angle (15 000 K/rad²
// ≈ 30 kcal/mol/rad², the order of a Gō-model virtual-bond angle) about 110°
// and free rotation, in place of the alkane chain terms.
const RESIDUE_BACKBONE: BeadType['bonded'] = { angleK: 15000, angleDeg: 110, torsionK: 0 };

export const GENERIC_BEAD_TYPES: readonly BeadType[] = [
  generic('B', 'polymer bead (one styrene unit)', '#3a9ad9', 104.15, 5.0, 930, { suggestedK: 450 }),
  // A rod's pair term is Gay-Berne (gayBerne.ts): its sphere–sphere pair is
  // switched off by a zero-depth repulsive rule.
  generic('ROD', '5CB-like rod (Gay-Berne, aspect 3.6)', '#d98c3a', 249.4, 5.0, 540, {
    aspect: 3.6,
    suggestedK: 293,
    pairs: { ROD: { epsilon: 0, repulsive: true } },
  }),
  // Residue beads carry a Cα-trace backbone of their own (RESIDUE_BACKBONE): a
  // soft harmonic angle and free rotation. Under the alkane chain terms (114°,
  // 62 500 K/rad², 3-fold torsion) a folding sequence stayed a loose knot: at
  // 72 residues the coil was only 1.1× the folder's size; with the soft angle
  // it is 1.5× and the hydrophobic beads sit 3.6 Å deeper than the polar ones
  // (0.6 ns probes, 2026-11-01).
  generic('H', 'hydrophobic residue', '#e8803a', 110, 5.0, 600, {
    bondAng: 3.8,
    suggestedK: 300,
    pairs: { P: REPULSIVE },
    bonded: RESIDUE_BACKBONE,
  }),
  generic('P', 'polar residue', '#9ecae1', 110, 5.0, 600, {
    bondAng: 3.8,
    suggestedK: 300,
    pairs: { P: REPULSIVE },
    bonded: RESIDUE_BACKBONE,
  }),
  // A WIDE head (7 Å on 4.7 Å tails): a cone-shaped surfactant, Israelachvili's
  // packing argument. With a head as thin as its tails (4.7 Å) 60 lipids pile
  // into one 59-lipid lump whose surface is half tails (60 heads cover half of
  // a 120-tail core), and it never looks like a micelle; at 7 Å they form 6–7
  // compact micelles of 17–25 lipids with every head outside and the heads
  // 4.5–4.9 Å beyond the tails, stable over 1 ns (probes 2026-10-31; 6 Å was
  // still one lump, 8 Å like 7). bondAng keeps the chain spacing at the tails'
  // 0.97 σ, so a wider head does not stretch the tail bonds.
  generic('HD', 'lipid head, hydrophilic', '#f2c94c', 72, 7.0, 420, {
    bondAng: 0.97 * 4.7,
    suggestedK: 300,
    pairs: { HD: REPULSIVE, TL: REPULSIVE },
  }),
  // 630 K, not Martini's 420 K (3.5 kJ/mol): with no water beads the tail–tail
  // well alone must carry the hydrophobic effect. At 420 K a 60-lipid box at
  // 300 K is a transient network that sheds and regains lipids for 2 ns
  // (largest cluster 18–52, 70–97 % of heads outside); at 630 K every lipid
  // joins one ball within 250 ps and stays (100 % of heads outside, tails 4 Å
  // deeper than heads at 2 ns). Checked headlessly 2026-10-31.
  generic('TL', 'lipid tail, hydrophobic', '#7f8c8d', 72, 4.7, 630, { suggestedK: 300 }),
  // A small polar head, no wider than the tails: HS TL TL TL is a cylinder, the
  // shape that fails to make micelles in water and forms inverse micelles in oil
  // (beadSolvent.ts holds the oil rules; in water it repels everything, as HD).
  generic('HS', 'lipid head, small, hydrophilic', '#e67e22', 72, 4.7, 420, {
    suggestedK: 300,
    pairs: { HS: REPULSIVE, HD: REPULSIVE, TL: REPULSIVE },
  }),
  // Gō-model residues (buildGoModel): one bead per Cα of a real structure. The
  // two types differ only in color — hydrophobic residues orange, polar pale
  // blue, as the HP beads — so the core of a real fold is visible; the physics
  // is the native-contact list in BeadModel.go, and every pair not on that list
  // repels (WCA at 4 Å). The bending / torsion terms come from the native
  // geometry (applyBeadBondedTerms), so no `bonded` entry here.
  generic('GH', 'residue, hydrophobic (Gō model)', '#e8803a', 110, 4.0, 300, {
    bondAng: 3.8,
    suggestedK: 300,
    pairs: { GH: REPULSIVE, GP: REPULSIVE },
  }),
  generic('GP', 'residue, polar (Gō model)', '#9ecae1', 110, 4.0, 300, {
    bondAng: 3.8,
    suggestedK: 300,
    pairs: { GH: REPULSIVE, GP: REPULSIVE },
  }),
];

/** The bond length a chain of these beads is built with: the first type's own, else 0.97 σ. */
export const defaultBondLengthAng = (tokens: readonly string[]): number => {
  const t = tokens.length > 0 ? BY_NAME.get(tokens[0]) : undefined;
  if (!t) return 0.97 * DEFAULT_BEAD_SCALE.sigma;
  return t.bondAng ?? 0.97 * t.sigma;
};

/** The temperature a lab is set to when a model of these beads is built, or undefined. */
export const suggestedTemperatureK = (tokens: readonly string[]): number | undefined =>
  tokens.length > 0 ? BY_NAME.get(tokens[0])?.suggestedK : undefined;

/** The single-letter bead codes a run of letters expands to ("HPHHPP" → H, P, H, H, P, P). */
const LETTER_CODES = new Set(['H', 'P']);

const BY_NAME = new Map<string, BeadType>();
for (const t of [...UNITED_ATOM_TYPES, ...GENERIC_BEAD_TYPES]) BY_NAME.set(t.name, t);

/** A built-in bead type by name ('CH2', 'B', 'ROD'), or undefined. */
export const beadTypeByName = (name: string): BeadType | undefined => BY_NAME.get(name);

/** The draw radius of a bead: its own, or half its σ. */
export const beadRadius = (t: BeadType): number => t.radius ?? t.sigma / 2;

/** The bead type of atom i of a model, or undefined past the end or for a bad index. */
export const beadTypeAt = (model: BeadModel, i: number): BeadType | undefined => {
  const k = model.typeOfAtom[i];
  return k === undefined ? undefined : model.types[k];
};

/**
 * Give every atom of a coarse-grained molecule the mass / σ / ε / charge of its
 * bead type (Atom.setBead). A molecule without a bead model is left alone, and
 * so is any atom past the end of the model's per-atom list. Idempotent; returns
 * how many atoms were set.
 */
export const applyBeadModelToMolecule = (m: {
  beads?: BeadModel;
  atoms?: Atom[];
  radialBonds?: { strength: number; length: number }[];
  angularBonds?: { strength: number; angle: number }[];
  torsionalBonds?: { strength: number; periodicity: number; dihedral: number }[];
  improperBonds?: { strength: number }[];
}): number => {
  const model = m.beads;
  if (!model || !m.atoms) return 0;
  let n = 0;
  for (let i = 0; i < m.atoms.length; i++) {
    const t = beadTypeAt(model, i);
    if (!t) continue;
    m.atoms[i].setBead(t);
    n++;
  }
  if (n > 0) applyBeadBondedTerms(m, model);
  return n;
};

/** The bonded constants of a bead model, in engine units (eV, Å, rad). */
export interface BeadBondedTerms {
  bondStrength: number; // eV/Å², V = ½ k (r − r₀)², r₀ = the built length
  angleStrength: number; // eV/rad², V = ½ k (θ − θ₀)²; 0 ⇒ a freely jointed chain
  angleRad: number; // θ₀
  torsionStrength: number; // eV, V = ½ s (1 − cos(n φ − δ)); 0 ⇒ free rotation
  torsionPeriodicity: number;
  torsionOffset: number; // δ
}

// TraPPE-UA flexible bond and angle (Martin & Siepmann 1998; the bond from the
// OPLS-UA / TraPPE-flexible value), and the 3-fold term of the OPLS alkane
// torsion c₃ = 791.32 K: V = c₃ (1 + cos 3φ) = ½ (2c₃)(1 − cos(3φ − π)).
const UNITED_ATOM_BONDED: BeadBondedTerms = {
  bondStrength: kelvinToEv(452900),
  angleStrength: kelvinToEv(62500),
  angleRad: (114 * Math.PI) / 180,
  torsionStrength: 2 * kelvinToEv(791.32),
  torsionPeriodicity: 3,
  torsionOffset: Math.PI,
};

/** The bonded terms of a bead model: united-atom TraPPE, or a generic harmonic chain in its scale. */
export const beadBondedTerms = (model: BeadModel): BeadBondedTerms => {
  if (model.resolution === 'united-atom' || model.units === 'real') return UNITED_ATOM_BONDED;
  const scale = model.scale ?? DEFAULT_BEAD_SCALE;
  return {
    bondStrength: (800 * scale.epsilon) / (scale.sigma * scale.sigma),
    angleStrength: 0,
    angleRad: Math.PI,
    torsionStrength: 0,
    torsionPeriodicity: 1,
    torsionOffset: 0,
  };
};

/**
 * Put a bead molecule's bonded terms on its bond objects. Bond lengths stay as
 * built (the typed bond length); angles go to the model's θ₀; torsions to its
 * one cosine term. Impropers (none for a chain, but the MMFF builder may have
 * made some for a branch) are switched off. Idempotent.
 */
export const applyBeadBondedTerms = (
  m: {
    radialBonds?: { strength: number; length: number }[];
    angularBonds?: { strength: number; angle: number; atom2?: { bead?: BeadType } }[];
    torsionalBonds?: {
      strength: number;
      periodicity: number;
      dihedral: number;
      atom2?: { bead?: BeadType };
      atom3?: { bead?: BeadType };
    }[];
    improperBonds?: { strength: number }[];
  },
  model: BeadModel,
): void => {
  const terms = beadBondedTerms(model);
  for (const b of m.radialBonds ?? []) b.strength = terms.bondStrength;
  // A Gō model: every angle and dihedral rests at its NATIVE value (the
  // geometry it was built with), with the model's own stiffness. The dihedral
  // is Clementi's K[(1 − cos(φ − φ₀)) + ½(1 − cos 3(φ − φ₀))], i.e. in the
  // engine's ½s(1 − cos(nφ − δ)) form a 1-fold term (s = 2K, δ = φ₀) plus a
  // 3-fold one (s = K, δ = 3φ₀) on the same four beads. Terms are matched by their
  // four atoms, NOT by periodicity: in the lab the viewer's MMFF torsion pack has
  // already set many torsions to periodicity 3 with its own phase, and treating
  // those as our 3-fold terms left the chain with no 1-fold native term at all (it
  // unfolded about 200 K early, in the lab only). Idempotent. builtDihedral is the
  // as-loaded φ₀ on every path.
  if (model.go) {
    const kA = kelvinToEv(model.go.angleK);
    const kD = kelvinToEv(model.go.dihedralK);
    for (const a of m.angularBonds ?? []) a.strength = kA; // a.angle stays as built
    const torsions = (m.torsionalBonds ?? []) as TorsionalBond[];
    const groups = new Map<string, TorsionalBond[]>();
    const ids = new Map<unknown, number>();
    const idOf = (a: unknown) => {
      if (!ids.has(a)) ids.set(a, ids.size);
      return ids.get(a)!;
    };
    for (const t of torsions) {
      const key = [t.atom1, t.atom2, t.atom3, t.atom4].map(idOf).join(',');
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key)!.push(t);
    }
    const rebuilt: TorsionalBond[] = [];
    for (const g of groups.values()) {
      const t1 = g[0];
      const phi0 = t1.builtDihedral;
      t1.periodicity = 1;
      t1.strength = 2 * kD;
      t1.dihedral = phi0;
      const t3 = g[1] ?? new TorsionalBond(t1.atom1, t1.atom2, t1.atom3, t1.atom4, phi0);
      t3.builtDihedral = phi0;
      t3.periodicity = 3;
      t3.strength = kD;
      t3.dihedral = 3 * phi0;
      rebuilt.push(t1, t3);
    }
    torsions.length = 0;
    torsions.push(...rebuilt);
    for (const im of m.improperBonds ?? []) im.strength = 0;
    return;
  }
  // A bead type may carry its own chain stiffness (BeadType.bonded): the angle
  // at a vertex of that type, and the torsion about an inner bond whose beads
  // are of that type, take it in place of the model's chain terms.
  for (const a of m.angularBonds ?? []) {
    const own = a.atom2?.bead?.bonded;
    const k = own?.angleK !== undefined ? kelvinToEv(own.angleK) : terms.angleStrength;
    a.strength = k;
    if (k > 0) a.angle = own?.angleDeg !== undefined ? (own.angleDeg * Math.PI) / 180 : terms.angleRad;
  }
  for (const t of m.torsionalBonds ?? []) {
    const own = t.atom2?.bead?.bonded ?? t.atom3?.bead?.bonded;
    t.strength = own?.torsionK !== undefined ? kelvinToEv(own.torsionK) : terms.torsionStrength;
    t.periodicity = terms.torsionPeriodicity;
    t.dihedral = terms.torsionOffset;
  }
  for (const im of m.improperBonds ?? []) im.strength = 0;
};

/**
 * Derive a bead molecule's angular and torsional bonds from its radial bonds
 * the way the lab does on load (dynamicsViewer: ModelUtil.generateAngularBonds
 * / generateTorsionalBonds, each term's rest geometry captured as built, a
 * torsion through a near-straight angle left out), then put the model's bonded
 * terms on them. A molecule from buildBeadChainTemplate has radial bonds only,
 * so a headless MolecularDynamics built straight from it (the example seeders,
 * tests) would run a freely jointed chain while the lab runs the semiflexible
 * one; call this on every molecule first. Replaces the derived lists; a
 * molecule without a bead model is left alone.
 */
// ── Gō model of a real protein (Clementi, Nymeyer & Onuchic 2000) ─────────────
// One bead per residue at the Cα of a PDB structure; the residue pairs within
// `contactCutoffAng` of each other and at least three residues apart along the
// chain are the native contacts, each a 12–10 well of depth `epsilonK` at its
// native distance (ContactBond); every other pair repels; bonds, angles and
// dihedrals rest at their native values. Built at the native structure, such a
// chain holds it (RMSD under 1 Å, Q near 1 at 300 K), which a two-letter HP chain
// cannot do, unfolds near T = ε/k and refolds on cooling.

const HYDROPHOBIC_RESIDUES = new Set(['ALA', 'VAL', 'ILE', 'LEU', 'MET', 'PHE', 'TRP', 'CYS', 'TYR', 'PRO']);
const ONE_LETTER: Record<string, string> = {
  ALA: 'A',
  ARG: 'R',
  ASN: 'N',
  ASP: 'D',
  CYS: 'C',
  GLN: 'Q',
  GLU: 'E',
  GLY: 'G',
  HIS: 'H',
  ILE: 'I',
  LEU: 'L',
  LYS: 'K',
  MET: 'M',
  PHE: 'F',
  PRO: 'P',
  SER: 'S',
  THR: 'T',
  TRP: 'W',
  TYR: 'Y',
  VAL: 'V',
};

export interface CaResidue {
  resName: string;
  resSeq: number;
  chain: string;
  key: string; // chain + resSeq + insertion code, as the PDB line has them
  x: number;
  y: number;
  z: number;
}

/** The Cα trace of one chain of a PDB file (first chain by default; first model; first altLoc). */
// Modified residues deposited as HETATM inside a chain, with the residue each one
// is a modification of. Without them a selenomethionine (nearly every
// selenium-phased crystal structure has several) reads as a hole in the chain.
const MODIFIED_RESIDUES: Readonly<Record<string, string>> = {
  MSE: 'MET',
  SEP: 'SER',
  TPO: 'THR',
  PTR: 'TYR',
  CSO: 'CYS',
  CME: 'CYS',
  HYP: 'PRO',
  MLY: 'LYS',
  M3L: 'LYS',
  KCX: 'LYS',
};
/** A chain residue's coordinate line: any ATOM, or a HETATM of a known modified residue. */
const isChainResidueLine = (line: string): boolean =>
  line.startsWith('ATOM') || (line.startsWith('HETATM') && line.substring(17, 20).trim() in MODIFIED_RESIDUES);

/** Above this Cα–Cα distance two consecutive residues of a file are not bonded (a peptide is 3.8 Å). */
export const GO_CHAIN_GAP_ANG = 4.5;

export const caTraceFromPdb = (pdbText: string, chainId?: string): CaResidue[] => {
  const out: CaResidue[] = [];
  const seen = new Set<string>();
  let chain = chainId;
  for (const line of pdbText.split('\n')) {
    if (line.startsWith('ENDMDL')) break;
    if (!isChainResidueLine(line)) continue;
    if (line.substring(12, 16).trim() !== 'CA') continue;
    const alt = line[16];
    if (alt !== ' ' && alt !== 'A') continue;
    const ch = line[21];
    if (chain === undefined) chain = ch;
    if (ch !== chain) continue;
    const key = `${ch}${line.substring(22, 27)}`;
    if (seen.has(key)) continue;
    seen.add(key);
    const rawName = line.substring(17, 20).trim();
    out.push({
      resName: MODIFIED_RESIDUES[rawName] ?? rawName,
      resSeq: Number(line.substring(22, 26)),
      chain: ch,
      key,
      x: Number(line.substring(30, 38)),
      y: Number(line.substring(38, 46)),
      z: Number(line.substring(46, 54)),
    });
  }
  return out;
};

/**
 * Heavy atoms of each residue of the Cα trace's chain, in the trace's order
 * (residues with no heavy atom beyond Cα get just the Cα). The native contacts
 * are defined from these — two residues are in contact when any two of their
 * heavy atoms are within `heavyCutoffAng` — because a Cα-only cutoff finds the
 * i,i+3 and i,i+4 pairs inside a helix and misses most of the pairs that hold
 * one helix to another; a model built on Cα contacts alone let a villin helix
 * swing away from the bundle at 300 K with 9 of 10 contacts "kept".
 */
const heavyAtomsByResidue = (pdbText: string, chainId: string): Map<string, { x: number; y: number; z: number }[]> => {
  const out = new Map<string, { x: number; y: number; z: number }[]>();
  for (const line of pdbText.split('\n')) {
    if (line.startsWith('ENDMDL')) break;
    if (!isChainResidueLine(line)) continue;
    if (line[21] !== chainId) continue;
    const alt = line[16];
    if (alt !== ' ' && alt !== 'A') continue;
    const element = (
      line.substring(76, 78).trim() || line.substring(12, 14).trim().replace(/[0-9]/g, '')
    ).toUpperCase();
    if (element === 'H' || element === 'D') continue;
    const key = `${line[21]}${line.substring(22, 27)}`;
    if (!out.has(key)) out.set(key, []);
    out.get(key)!.push({
      x: Number(line.substring(30, 38)),
      y: Number(line.substring(38, 46)),
      z: Number(line.substring(46, 54)),
    });
  }
  return out;
};

export const buildGoModel = (opts: {
  pdbText: string;
  name: string;
  chain?: string;
  source?: string; // 'PDB 1YRF'
  // Well depth per native contact. With a canonical thermostat (csvr) Q crosses
  // 0.5 near T = ε/k: villin at ε 500 K ≈ 570 K, 700 K ≈ 690 K, 1000 K ≈ 1020 K,
  // 1500 K ≈ 1480 K, reversibly. At 300 K, ε 500 fluctuates (RMSD up to 3.8 Å,
  // Q 0.95) and ε 700 holds (RMSD 0.8 Å, Q 0.98). Default 700 K (probes 2026-11-01).
  // (Probes before that day ran the engine's default instant rescale, a flying
  // ice cube with an internal temperature near 50 K: their numbers are void.)
  epsilonK?: number;
  angleK?: number; // default 20 ε per rad² (Clementi)
  dihedralK?: number; // default ε (Clementi: K₁ = ε, K₃ = ε/2)
  heavyCutoffAng?: number; // default 4.5 Å between any two heavy atoms of the two residues
  contactCutoffAng?: number; // default 8 Å between Cα (Cα-only files)
}): {
  molecule: Molecule;
  residues: number;
  contacts: number;
  sequence: string;
  // Where the file's chain is broken: unresolved loops. No bond spans one.
  gaps: { afterResidue: number; beforeResidue: number; distanceAng: number }[];
  // Pieces with no bond and no native contact to the largest piece: they float free.
  detachedPieces: number;
} => {
  const trace = caTraceFromPdb(opts.pdbText, opts.chain);
  if (trace.length < 4) throw new Error('beadModel: fewer than four Cα in the structure');
  const cx = trace.reduce((s, r) => s + r.x, 0) / trace.length;
  const cy = trace.reduce((s, r) => s + r.y, 0) / trace.length;
  const cz = trace.reduce((s, r) => s + r.z, 0) / trace.length;
  const beads: BeadSpecBead[] = trace.map((r) => ({
    type: HYDROPHOBIC_RESIDUES.has(r.resName) ? 'GH' : 'GP',
    x: r.x - cx,
    y: r.y - cy,
    z: r.z - cz,
  }));
  const bonds: [number, number][] = [];
  // Unresolved loops leave a hole: a bond across one would be a long stiff rod
  // resting at the wrong length, so the pieces either side stay unbonded and are
  // held together by their native contacts alone.
  const gaps: { afterResidue: number; beforeResidue: number; distanceAng: number }[] = [];
  for (let i = 0; i + 1 < beads.length; i++) {
    const d = Math.hypot(beads[i + 1].x - beads[i].x, beads[i + 1].y - beads[i].y, beads[i + 1].z - beads[i].z);
    if (d > GO_CHAIN_GAP_ANG)
      gaps.push({
        afterResidue: trace[i].resSeq,
        beforeResidue: trace[i + 1].resSeq,
        distanceAng: Math.round(d * 10) / 10,
      });
    else bonds.push([i, i + 1]);
  }
  // Native contacts: residue pairs at least three apart along the chain with
  // any two heavy atoms within `heavyCutoffAng` (4.5 Å), r₀ = their Cα–Cα
  // distance. A Cα-only file (no side chains) falls back to Cα within
  // `contactCutoffAng` (8 Å).
  const heavy = heavyAtomsByResidue(opts.pdbText, trace[0].chain);
  const heavyCount = trace.reduce((n, r) => n + (heavy.get(r.key)?.length ?? 0), 0);
  const useHeavy = heavyCount > 2 * trace.length;
  const heavyCutoff = opts.heavyCutoffAng ?? 4.5;
  const caCutoff = opts.contactCutoffAng ?? 8;
  const contacts: number[] = [];
  for (let i = 0; i < beads.length; i++)
    for (let j = i + 3; j < beads.length; j++) {
      const rCa = Math.hypot(beads[i].x - beads[j].x, beads[i].y - beads[j].y, beads[i].z - beads[j].z);
      let touching = false;
      if (useHeavy) {
        const ai = heavy.get(trace[i].key) ?? [];
        const aj = heavy.get(trace[j].key) ?? [];
        const cut2 = heavyCutoff * heavyCutoff;
        outer: for (const p of ai)
          for (const q of aj) {
            const dx = p.x - q.x;
            const dy = p.y - q.y;
            const dz = p.z - q.z;
            if (dx * dx + dy * dy + dz * dz < cut2) {
              touching = true;
              break outer;
            }
          }
      } else touching = rCa < caCutoff;
      if (touching) contacts.push(i, j, Math.round(rCa * 1000) / 1000);
    }
  const sequence = trace.map((r) => ONE_LETTER[r.resName] ?? 'X').join('');
  // Connected pieces through bonds and native contacts.
  const root = beads.map((_, i) => i);
  const find = (i: number): number => (root[i] === i ? i : (root[i] = find(root[i])));
  for (const [i, j] of bonds) root[find(i)] = find(j);
  for (let k = 0; k + 2 < contacts.length; k += 3) root[find(contacts[k])] = find(contacts[k + 1]);
  const detachedPieces = new Set(beads.map((_, i) => find(i))).size - 1;
  const m = buildBeadMolecule({ name: opts.name, resolution: 'bead-spring', beads, bonds, units: 'real' });
  const eps = opts.epsilonK ?? 700;
  m.beads!.go = {
    contacts,
    native: beads.flatMap((b) => [b.x, b.y, b.z].map((v) => Math.round(v * 1000) / 1000)),
    epsilonK: eps,
    angleK: opts.angleK ?? 20 * eps,
    dihedralK: opts.dihedralK ?? eps,
    source: `${opts.source ?? 'PDB'} chain ${trace[0].chain}`,
    sequence,
  };
  return { molecule: m, residues: trace.length, contacts: contacts.length / 3, sequence, gaps, detachedPieces };
};

/** The native-contact list of a Gō molecule as FoldContact-shaped triples. */
export const goContactsOf = (model: BeadModel | undefined): { i: number; j: number; r0: number }[] => {
  const c = model?.go?.contacts ?? [];
  const out: { i: number; j: number; r0: number }[] = [];
  for (let k = 0; k + 2 < c.length; k += 3) out.push({ i: c[k], j: c[k + 1], r0: c[k + 2] });
  return out;
};

export const deriveBeadTopology = (m: Molecule): void => {
  if (!m.beads) return;
  const aTuples = ModelUtil.generateAngularBonds(m.atoms, m.radialBonds);
  m.angularBonds = aTuples.map((t) => new AngularBond(m.atoms[t.i], m.atoms[t.j], m.atoms[t.k]));
  const tTuples = ModelUtil.generateTorsionalBonds(m.atoms, m.radialBonds, aTuples);
  m.torsionalBonds = tTuples.map((q) => new TorsionalBond(m.atoms[q.i], m.atoms[q.j], m.atoms[q.k], m.atoms[q.l]));
  m.improperBonds = [];
  applyBeadBondedTerms(m, m.beads);
};

/** applyBeadModelToMolecule over a lab; returns how many molecules had a model. */
export const applyBeadModel = (molecules: readonly { beads?: BeadModel; atoms?: Atom[] }[]): number => {
  let n = 0;
  for (const m of molecules) if (applyBeadModelToMolecule(m) > 0) n++;
  return n;
};

/**
 * The resolution a set of molecules runs at: 'all-atom' when none carries a
 * bead model, else the resolution of the first that does (a lab mixing
 * resolutions reports the first; the chip will say "mixed" in todo #2).
 */
export const resolutionOf = (molecules: readonly { beads?: BeadModel }[] | undefined): ParticleResolution => {
  if (!molecules) return 'all-atom';
  for (const m of molecules) if (m.beads) return m.beads.resolution;
  return 'all-atom';
};

/** Is this molecule record coarse-grained? */
export const isBeadMolecule = (m: Pick<MoleculeInterface, 'beads'> | undefined | null): boolean => !!m?.beads;

// ---------------------------------------------------------------------------
// Building a bead molecule: placeholder SDF + bead model.

export interface BeadSpecBead {
  type: string; // a built-in bead type name, or one in BeadSpec.types
  x: number;
  y: number;
  z: number;
}

export interface BeadSpec {
  name: string;
  resolution: BeadModel['resolution'];
  beads: BeadSpecBead[];
  bonds?: [number, number][]; // bead indices
  types?: BeadType[]; // custom types, looked up before the built-ins
  units?: BeadModel['units']; // default: 'real' for united-atom, 'reduced' otherwise
  scale?: BeadModel['scale']; // default: DEFAULT_BEAD_SCALE for reduced units
  parent?: string;
}

// V2000 field widths — the same conventions as polymerBuilder.ts / crystalSdf.ts;
// the SDF parser is unforgiving about columns.
const f10 = (n: number): string => (Number.isFinite(n) ? n.toFixed(4) : '0.0000').padStart(10, ' ');
const i3 = (n: number): string => n.toString().padStart(3, ' ');
const elem3 = (sym: string): string => (sym.length > 2 ? sym.slice(0, 2) : sym).padEnd(3, ' ');

/** A V2000 SDF of beads at their placeholder elements, single-bonded as listed. */
export const beadsToSdf = (
  name: string,
  beads: readonly { symbol: string; x: number; y: number; z: number }[],
  bonds: readonly [number, number][],
): string => {
  const lines: string[] = [];
  lines.push(name.slice(0, 80));
  lines.push('  AIMS   beads');
  lines.push('');
  lines.push(`${i3(beads.length)}${i3(bonds.length)}  0  0  0  0  0  0  0  0999 V2000`);
  for (const b of beads) {
    lines.push(`${f10(b.x)}${f10(b.y)}${f10(b.z)} ${elem3(b.symbol)} 0  0  0  0  0  0  0  0  0  0  0  0`);
  }
  for (const [i, j] of bonds) lines.push(`${i3(i + 1)}${i3(j + 1)}  1  0  0  0  0`);
  lines.push('M  END');
  lines.push('$$$$');
  return lines.join('\n');
};

/**
 * Build a coarse-grained Molecule from a spec: atoms at the beads' placeholder
 * elements with the bead parameters applied, radial bonds as listed, the
 * placeholder SDF in `data` (so the gallery, the lab loader and the thumbnail
 * all parse it as any other molecule) and the bead model in `beads`.
 */
export const buildBeadMolecule = (spec: BeadSpec): Molecule => {
  const custom = new Map<string, BeadType>();
  for (const t of spec.types ?? []) custom.set(t.name, t);
  const types: BeadType[] = [];
  const indexOfType = new Map<string, number>();
  const typeOfAtom: number[] = [];
  const atoms: Atom[] = [];
  for (let i = 0; i < spec.beads.length; i++) {
    const b = spec.beads[i];
    const t = custom.get(b.type) ?? BY_NAME.get(b.type);
    if (!t) throw new Error(`beadModel: unknown bead type "${b.type}"`);
    let k = indexOfType.get(t.name);
    if (k === undefined) {
      k = types.length;
      types.push(t);
      indexOfType.set(t.name, k);
    }
    typeOfAtom.push(k);
    const a = new Atom(i, t.symbol, new Vector3(b.x, b.y, b.z), true);
    a.setBead(t);
    atoms.push(a);
  }
  const bonds = spec.bonds ?? [];
  const m = new Molecule(spec.name, atoms);
  for (const [i, j] of bonds) {
    if (atoms[i] && atoms[j]) m.radialBonds.push(new RadialBond(atoms[i], atoms[j]));
  }
  const units = spec.units ?? 'real';
  m.beads = {
    resolution: spec.resolution,
    units,
    types,
    typeOfAtom,
    ...(units === 'reduced' ? { scale: spec.scale ?? DEFAULT_BEAD_SCALE } : {}),
    ...(spec.parent ? { parent: spec.parent } : {}),
  };
  m.data = beadsToSdf(
    spec.name,
    atoms.map((a) => ({ symbol: a.elementSymbol, x: a.position.x, y: a.position.y, z: a.position.z })),
    bonds,
  );
  m.updateCenter();
  return m;
};

/**
 * A collection of particles stored as one molecule: several beads or rods with no
 * bonds between them (ROD400). Its particles, not the molecule, are what the user
 * sees and picks, and each can be removed without touching the others.
 */
export const isParticleCollection = (
  m: Pick<MoleculeInterface, 'beads'> & { atoms?: unknown[]; radialBonds?: unknown[] },
): boolean => !!m.beads && (m.atoms?.length ?? 0) > 1 && (m.radialBonds?.length ?? 0) === 0;

/**
 * The collection `source` with exactly these particles: atoms re-indexed, the
 * per-atom bead list, charges and SDF rebuilt to match, and a count in the name
 * (ROD400) kept true. `atoms` become the new molecule's, so pass ones not in a store.
 */
export const withParticles = (source: Molecule, atoms: Atom[], typeOfAtom: number[]): Molecule => {
  const count = /^(.*?)(\d+)$/.exec(source.name);
  const name = count && Number(count[2]) === source.atoms.length ? `${count[1]}${atoms.length}` : source.name;
  const m = new Molecule(name, atoms);
  atoms.forEach((a, i) => (a.index = i));
  m.style = source.style;
  m.overlayStyle = source.overlayStyle;
  m.autoBond = source.autoBond;
  m.beads = { ...source.beads!, typeOfAtom };
  if (source.atomCharges) m.atomCharges = atoms.map((a) => a.charge);
  m.data = beadsToSdf(
    name,
    atoms.map((a) => ({ symbol: a.elementSymbol, x: a.position.x, y: a.position.y, z: a.position.z })),
    [],
  );
  m.updateCenter();
  return m;
};

/**
 * A straight chain of `count` beads of one type, `bondLength` apart along
 * `direction` from `origin` — the simplest bead-spring or united-atom chain,
 * what the tests and the Build Chain dialog (todo #11) start from.
 */
export const buildLinearBeadChain = (opts: {
  name: string;
  type: string;
  count: number;
  bondLength: number;
  resolution?: BeadModel['resolution'];
  origin?: Vector3;
  direction?: Vector3;
  types?: BeadType[];
  parent?: string;
}): Molecule => {
  const o = opts.origin ?? new Vector3();
  const d = (opts.direction ?? new Vector3(1, 0, 0)).clone().normalize();
  const beads: BeadSpecBead[] = [];
  const bonds: [number, number][] = [];
  for (let i = 0; i < opts.count; i++) {
    const s = i * opts.bondLength;
    beads.push({ type: opts.type, x: o.x + d.x * s, y: o.y + d.y * s, z: o.z + d.z * s });
    if (i > 0) bonds.push([i - 1, i]);
  }
  const resolution = opts.resolution ?? (isUnitedAtom(opts.type) ? 'united-atom' : 'bead-spring');
  return buildBeadMolecule({
    name: opts.name,
    resolution,
    beads,
    bonds,
    types: opts.types,
    parent: opts.parent,
  });
};

const isUnitedAtom = (typeName: string): boolean => UNITED_ATOM_TYPES.some((t) => t.name === typeName);

// ---------------------------------------------------------------------------
// What the atom-assuming surfaces show for a bead (todo #2): names, the
// composition line, the lab's resolution, draw radius and color.

const SUBSCRIPT_DIGITS = '₀₁₂₃₄₅₆₇₈₉';

/** 'CH2' → 'CH₂', 'B' → 'B': a bead name with its trailing count as a subscript. */
export const beadDisplayName = (t: Pick<BeadType, 'name'>): string =>
  t.name.replace(/(\d+)$/, (d) => [...d].map((c) => SUBSCRIPT_DIGITS[Number(c)]).join(''));

/** Counts of bead types over a list of atoms, in order of first appearance. */
const beadCounts = (atoms: readonly { bead?: BeadType }[]): Map<string, number> => {
  const counts = new Map<string, number>();
  for (const a of atoms) {
    if (!a.bead) continue;
    counts.set(a.bead.name, (counts.get(a.bead.name) ?? 0) + 1);
  }
  return counts;
};

/** '10 CH₂ · 2 CH₃': the composition line that replaces a formula for a bead molecule. */
export const beadComposition = (model: BeadModel): string => {
  const counts = new Map<number, number>();
  for (const k of model.typeOfAtom) counts.set(k, (counts.get(k) ?? 0) + 1);
  return [...counts.entries()]
    .filter(([k]) => model.types[k])
    .map(([k, n]) => `${n} ${beadDisplayName(model.types[k])}`)
    .join(' · ');
};

export interface LabResolution {
  resolution: ParticleResolution;
  mixed: boolean; // molecules of more than one resolution (all-atom included)
  reduced: boolean; // some bead model is in reduced units
  composition: string; // bead counts over the lab, '' when all-atom
}

/**
 * The resolution a lab of molecules runs at, for the chip, the Content panel
 * and the agent: the single resolution when every molecule shares one, else
 * the first coarse-grained one with `mixed` set.
 */
export const labResolution = (
  molecules: readonly { beads?: BeadModel; atoms?: readonly { bead?: BeadType }[] }[] | undefined,
): LabResolution => {
  const list = molecules ?? [];
  const seen = new Set<ParticleResolution>();
  let reduced = false;
  for (const m of list) {
    seen.add(m.beads?.resolution ?? 'all-atom');
    if (m.beads?.units === 'reduced') reduced = true;
  }
  const resolution = resolutionOf(list);
  const counts = beadCounts(list.flatMap((m) => m.atoms ?? []));
  const composition = [...counts.entries()].map(([name, n]) => `${n} ${beadDisplayName({ name })}`).join(' · ');
  return { resolution, mixed: seen.size > 1, reduced, composition };
};

/** The radius the lab draws (and hit-tests) an atom at: its bead's, or its element's. */
export const drawRadiusOf = (a: Atom): number =>
  a.bead ? beadRadius(a.bead) : Element.getByName(a.elementSymbol).radius;

/** A bead type's color as the number the chem-lib palette uses, or undefined. */
export const beadColorValue = (t: BeadType): number | undefined => {
  if (!t.color) return undefined;
  const n = parseInt(t.color.replace(/^#/, ''), 16);
  return Number.isFinite(n) ? n : undefined;
};

/**
 * Make a parsed chem-lib complex draw its atoms as beads: each atom of a bead
 * molecule gets an element view whose radius is the bead's draw radius (the
 * ball-and-stick and space-filling modes read atom.element.radius), and, for a
 * bead type with its own color, a `beadColor` the element colorer returns
 * instead of the placeholder element's. The element's name and identity-free
 * fields are inherited, so bond perception, the formula and the engine's
 * placeholder symbol are unchanged. Idempotent.
 */
export const decorateBeadComplex = (
  result: { _atoms?: unknown[] } | null | undefined,
  model: BeadModel,
  opts: { rodsAsSpheres?: boolean } = {},
): void => {
  const atoms = result?._atoms as { element: { radius: number }; beadColor?: number }[] | undefined;
  if (!atoms) return;
  for (let i = 0; i < atoms.length; i++) {
    const t = beadTypeAt(model, i);
    if (!t) continue;
    const atom = atoms[i];
    // A rod is drawn as an ellipsoid by the lab viewer (gayBerneRods.tsx); its
    // sphere vanishes there, but a card thumbnail has no ellipsoid layer.
    const radius = (t.aspect ?? 1) > 1 && !opts.rodsAsSpheres ? 0.001 : beadRadius(t);
    if (atom.element.radius !== radius) {
      atom.element = Object.create(atom.element, { radius: { value: radius, enumerable: true } });
    }
    const color = beadColorValue(t);
    if (color !== undefined) atom.beadColor = color;
    else delete atom.beadColor;
  }
};

/**
 * An aligned box of rods (todo #4): N Gay-Berne rods on a layered lattice along
 * ẑ at the reduced number density ρ* = N σ₀³ / V — the nematic / smectic start
 * of a liquid-crystal experiment (heat it to clear, cool it to reorder, switch
 * the field). Rods within a layer sit on a hexagonal grid at contact (σ₀); the
 * layers are stacked end to end, κσ₀ apart, which caps the lattice at
 * ρ* = 1/(κ·√3/2) ≈ 0.32 for 5CB — and that is 5CB's real number density
 * (1.008 g/cm³ at 249 g/mol with σ₀ = 5 Å gives ρ* 0.30). An offset ABAB stack
 * with each tip in the hollow of the next layer packs denser (ρ* 0.39) but was
 * tried and rejected: it lost its order at every well depth. Each rod is tilted
 * by up to `tiltDeg` at random. Returns the template, the box that holds the
 * lattice and the density it reached.
 */
export const buildRodBoxTemplate = (opts: {
  count: number;
  densityReduced?: number;
  type?: string;
  // A rod type of any size (rodTypeOfSize), in place of a built-in type by name.
  rodType?: BeadType;
  tiltDeg?: number;
  contactFactor?: number; // lattice spacing in σ₀, side by side and at the tips; default 1.0 (contact)
  random?: () => number;
  // A lattice for a PERIODIC cell: the box is exactly the lattice periods (no
  // half-spacing margins), so the hexagonal rows and the layers continue across
  // every face without a seam; the row count is made even for the same reason.
  periodic?: boolean;
}): BeadChainTemplate & {
  edgeAng: number;
  boxAng: { lx: number; ly: number; lz: number };
  achievedDensity: number;
} => {
  const rnd = opts.random ?? Math.random;
  const t = opts.rodType ?? BY_NAME.get(opts.type ?? 'ROD');
  if (!t || (t.aspect ?? 1) <= 1) throw new Error('beadModel: buildRodBoxTemplate needs a rod type');
  const n = Math.max(1, Math.round(opts.count));
  const rho = opts.densityReduced ?? 0.3;
  const s0 = t.sigma;
  const kappa = t.aspect!;
  const v = (n * s0 * s0 * s0) / rho; // the lattice volume
  // Layers one end-to-end minimum apart; within a layer a hexagonal (triangular)
  // grid, never closer than the side-by-side minimum: a lattice at contact (σ₀)
  // sits on the steep wall of the pair potential and flies apart on the first
  // step. Hexagonal rows at the minimum reach ρ* ≈ 0.3, the nematic's density.
  // At contact (σ₀ side by side, the same at the tips): the pair energy is zero
  // there and the lattice relaxes outward a little, which puts a 128-rod box at
  // ρ* ≈ 0.33 — nematic below T* ≈ 0.6 (checked), isotropic above. A lattice
  // at the pair minima (1.12 σ₀) stops near ρ* 0.23, where it never orders.
  const contact = opts.contactFactor ?? 1.0;
  const layer = (kappa - 1 + contact) * s0;
  // The layer count that makes the block most cubic at the spacing the density
  // asks for (floored at the side-by-side minimum).
  // Layers are nx × ny (rectangular, so few sites stay empty), nz of them.
  let best = {
    nz: 1,
    nx: n,
    ny: 1,
    spacing: contact * s0,
    rowPitch: (contact * s0 * Math.sqrt(3)) / 2,
    score: Infinity,
  };
  for (let nzTry = 1; nzTry <= n; nzTry++) {
    const per = Math.ceil(n / nzTry);
    const nxTry = Math.ceil(Math.sqrt(per));
    const nyTry = Math.ceil(per / nxTry);
    const area = v / (nzTry * layer);
    const sp = Math.max(contact * s0, Math.sqrt(area / (nxTry * nyTry * (Math.sqrt(3) / 2))));
    const rp = (sp * Math.sqrt(3)) / 2;
    const dims = [nxTry * sp + sp / 2, nyTry * rp + (sp - rp), nzTry * layer];
    const score = Math.max(...dims) / Math.min(...dims);
    if (score < best.score) best = { nz: nzTry, nx: nxTry, ny: nyTry, spacing: sp, rowPitch: rp, score };
    if (nzTry * layer > 2 * Math.cbrt(v)) break;
  }
  const periodic = opts.periodic ?? false;
  const { nz, nx } = best;
  let { ny, spacing, rowPitch } = best;
  if (periodic && ny % 2 === 1) {
    // One more row keeps the ABAB offset continuous across the y faces; the
    // spacing is re-derived so the cell still has the requested density.
    ny += 1;
    const area = v / (nz * layer);
    spacing = Math.max(contact * s0, Math.sqrt(area / (nx * ny * (Math.sqrt(3) / 2))));
    rowPitch = (spacing * Math.sqrt(3)) / 2;
  }
  const tilt = ((opts.tiltDeg ?? 5) * Math.PI) / 180;
  const beads: BeadSpecBead[] = [];
  const orientations: Vector3[] = [];
  // Walled: the lattice is centred with half a spacing of margin. Periodic: each
  // site sits a quarter spacing / half a pitch / half a layer in from the cell's
  // −x / −y / −z faces, so the image of the last row lands exactly one period on.
  const x0 = periodic ? (nx * spacing) / 2 - spacing / 4 : ((nx - 1) * spacing + spacing / 2) / 2;
  const y0 = periodic ? (ny * rowPitch) / 2 - rowPitch / 2 : ((ny - 1) * rowPitch) / 2;
  const z0 = periodic ? (nz * layer) / 2 - layer / 2 : ((nz - 1) * layer) / 2;
  // When n is short of the lattice, the empty sites are spread evenly through it
  // (site s is filled when ⌊(s + 1)·n / N⌋ steps up), not left in one corner of the
  // last layer, where filling in order put them all: a visible hole in the cell.
  const sites = nx * ny * nz;
  let s = 0;
  outer: for (let k = 0; k < nz; k++) {
    for (let j = 0; j < ny; j++) {
      for (let i = 0; i < nx; i++, s++) {
        if (beads.length >= n) break outer;
        if (Math.floor(((s + 1) * n) / sites) === Math.floor((s * n) / sites)) continue;
        beads.push({
          type: t.name,
          x: i * spacing + (j % 2) * (spacing / 2) - x0,
          y: j * rowPitch - y0,
          z: k * layer - z0,
        });
        const a = tilt * rnd();
        const phi = 2 * Math.PI * rnd();
        orientations.push(new Vector3(Math.sin(a) * Math.cos(phi), Math.sin(a) * Math.sin(phi), Math.cos(a)));
      }
    }
  }
  const molecule = buildBeadMolecule({
    name: `${t.name}${n}`,
    resolution: 'ellipsoid',
    beads,
    ...(opts.rodType ? { types: [opts.rodType] } : {}),
  });
  molecule.atoms.forEach((a, i) => {
    a.orientation?.copy(orientations[i]);
    a.initialOrientation?.copy(orientations[i]);
  });
  const boxAng = periodic
    ? { lx: nx * spacing, ly: ny * rowPitch, lz: nz * layer }
    : { lx: nx * spacing + spacing / 2, ly: ny * rowPitch + (spacing - rowPitch), lz: nz * layer };
  const edgeAng = Math.max(boxAng.lx, boxAng.ly, boxAng.lz);
  const achievedDensity = (n * s0 * s0 * s0) / (boxAng.lx * boxAng.ly * boxAng.lz);
  return { molecule, chainHeadAtomIndex: 0, chainTailAtomIndex: n - 1, edgeAng, boxAng, achievedDensity };
};

/**
 * A periodic nematic lattice of host rods with long dopant rods in it. Each dopant
 * takes the place of two host rods stacked end to end, centered on their midpoint,
 * so it must be no longer than two host layers. The cell's cross section is then
 * shrunk to the packing fraction of the undoped lattice: long rods at a fixed rod
 * density would fill more of the cell, and the extra order that brings would read
 * as an effect of the dopant.
 */
export const buildDopedRodCell = (opts: {
  sites: number;
  densityReduced: number;
  dopant: BeadType;
  dopantCount: number;
  tiltDeg?: number;
  random?: () => number;
}): BeadChainTemplate & {
  edgeAng: number;
  boxAng: { lx: number; ly: number; lz: number };
  achievedDensity: number;
  packingFraction: number;
  hostCount: number;
  dopantCount: number;
} => {
  const rnd = opts.random ?? Math.random;
  const host = BY_NAME.get('ROD')!;
  const box = buildRodBoxTemplate({
    count: opts.sites,
    densityReduced: opts.densityReduced,
    tiltDeg: opts.tiltDeg,
    periodic: true,
    random: rnd,
  });
  const atoms = box.molecule.atoms;
  const key = (a: Atom): string => a.position.x.toFixed(3) + ',' + a.position.y.toFixed(3);
  const columns = new Map<string, number[]>();
  atoms.forEach((a, i) => {
    const c = columns.get(key(a));
    if (c) c.push(i);
    else columns.set(key(a), [i]);
  });
  const zs = [...new Set(atoms.map((a) => a.position.z.toFixed(3)))].map(Number).sort((p, q) => p - q);
  const layer = zs.length > 1 ? zs[1] - zs[0] : box.boxAng.lz;
  const dopantLength = opts.dopant.sigma * (opts.dopant.aspect ?? 1);
  if (zs.length < 2 || dopantLength > 2 * layer + 1e-9)
    throw new Error('beadModel: a dopant must fit in two stacked host layers');
  // Pairs of stacked sites, each site used once.
  const pairs: [number, number][] = [];
  for (const column of columns.values()) {
    column.sort((p, q) => atoms[p].position.z - atoms[q].position.z);
    const start = column.length === zs.length && rnd() < 0.5 ? 1 : 0;
    for (let k = start; k + 1 < column.length; k += 2) pairs.push([column[k], column[k + 1]]);
  }
  for (let i = pairs.length - 1; i > 0; i--) {
    const j = Math.floor(rnd() * (i + 1));
    [pairs[i], pairs[j]] = [pairs[j], pairs[i]];
  }
  const dopantCount = Math.max(0, Math.round(opts.dopantCount));
  if (dopantCount > pairs.length) throw new Error('beadModel: too many dopant rods for this lattice');
  const used = new Set<number>();
  const beads: BeadSpecBead[] = [];
  const orientations: Vector3[] = [];
  for (const [p, q] of pairs.slice(0, dopantCount)) {
    used.add(p);
    used.add(q);
    const A = atoms[p].position;
    const B = atoms[q].position;
    beads.push({ type: opts.dopant.name, x: A.x, y: A.y, z: (A.z + B.z) / 2 });
    orientations.push(atoms[p].orientation!.clone());
  }
  atoms.forEach((a, i) => {
    if (used.has(i)) return;
    beads.push({ type: host.name, x: a.position.x, y: a.position.y, z: a.position.z });
    orientations.push(a.orientation!.clone());
  });
  const hostCount = beads.length - dopantCount;
  const rodVolume = (t: BeadType): number => (Math.PI / 6) * (t.aspect ?? 1) * t.sigma * t.sigma * t.sigma;
  const solids = hostCount * rodVolume(host) + dopantCount * rodVolume(opts.dopant);
  const undopedVolume = box.boxAng.lx * box.boxAng.ly * box.boxAng.lz;
  const shrink = Math.sqrt(solids / (opts.sites * rodVolume(host)));
  const boxAng = { lx: box.boxAng.lx * shrink, ly: box.boxAng.ly * shrink, lz: box.boxAng.lz };
  for (const b of beads) {
    b.x *= shrink;
    b.y *= shrink;
  }
  const molecule = buildBeadMolecule({
    name: dopantCount > 0 ? 'RODMIX' + beads.length : host.name + beads.length,
    resolution: 'ellipsoid',
    beads,
    types: dopantCount > 0 ? [host, opts.dopant] : [host],
  });
  molecule.atoms.forEach((a, i) => {
    a.orientation?.copy(orientations[i]);
    a.initialOrientation?.copy(orientations[i]);
  });
  const volume = boxAng.lx * boxAng.ly * boxAng.lz;
  return {
    molecule,
    chainHeadAtomIndex: 0,
    chainTailAtomIndex: beads.length - 1,
    edgeAng: Math.max(boxAng.lx, boxAng.ly, boxAng.lz),
    boxAng,
    achievedDensity: (beads.length * host.sigma ** 3) / volume,
    packingFraction: solids / volume,
    hostCount,
    dopantCount,
  };
};

// ---------------------------------------------------------------------------
// Reduced units (todo #3). A generic bead model is defined with σ = ε = m = 1
// and runs in the engine on BeadModel.scale; the charts divide the engine's
// fs / K / Å / eV by these to show t*, T*, r* and E*.

/** fs in one Å·√(g/mol / eV): the engine value of the reduced time unit τ = σ·√(m/ε). */
export const TAU_FS_PER_UNIT = 10.1805;

export interface ReducedUnits {
  sigma: number; // Å per σ
  epsilon: number; // eV per ε
  mass: number; // g/mol per m
  tauFs: number; // fs per τ
  tempK: number; // K per ε/k
  speedMps: number; // m/s per σ/τ
  summary: string; // 'σ = 3.4 Å · ε/k = 120 K · τ = 2.15 ps'
}

/** The reduced-unit scale of a BeadModel.scale, or of the default scale. */
export const reducedUnitsFromScale = (scale: NonNullable<BeadModel['scale']> = DEFAULT_BEAD_SCALE): ReducedUnits => {
  const tauFs = TAU_FS_PER_UNIT * scale.sigma * Math.sqrt(scale.mass / scale.epsilon);
  const tempK = evToKelvin(scale.epsilon);
  const speedMps = (scale.sigma / tauFs) * 1e5; // Å/fs → m/s
  const summary = `σ = ${scale.sigma} Å · ε/k = ${tempK.toFixed(0)} K · τ = ${(tauFs / 1000).toFixed(2)} ps`;
  return { sigma: scale.sigma, epsilon: scale.epsilon, mass: scale.mass, tauFs, tempK, speedMps, summary };
};

/**
 * The reduced-unit scale a lab's charts should use: that of the first molecule
 * whose bead model is in reduced units, or null when none is (an all-atom or
 * united-atom lab stays in fs / K / Å).
 */
export const reducedUnitsOf = (molecules: readonly { beads?: BeadModel }[] | undefined): ReducedUnits | null => {
  for (const m of molecules ?? []) {
    if (m.beads?.units === 'reduced') return reducedUnitsFromScale(m.beads.scale ?? DEFAULT_BEAD_SCALE);
  }
  return null;
};

// ---------------------------------------------------------------------------
// Building a bead chain from a typed spec (todo #11): the Build Chain dialog
// and the agent's build tool both come through here.

/**
 * Parse a bead sequence: bead type names separated by spaces, commas or
 * hyphens, each with an optional repeat ("CH3 CH2*8 CH3", "B", "H-P-P-H",
 * "B x20"). Returns the expanded token list, or an error naming the first
 * unknown type. An empty string parses to no tokens.
 */
export const parseBeadSequence = (
  text: string,
  custom: readonly BeadType[] = [],
): { tokens: string[]; error?: string } => {
  const known = new Set<string>([...BY_NAME.keys(), ...custom.map((t) => t.name)]);
  const tokens: string[] = [];
  // A repeat may be written with a space before it ("B x20", "CH2 * 8"): pull it
  // onto its bead before splitting on the separators.
  const joined = text.replace(/\s*([*×])\s*(\d+)/g, '*$2').replace(/\s+x\s*(\d+)(?=$|[\s,\-–])/gi, '*$1');
  for (const raw of joined.trim().split(/[\s,\-–]+/)) {
    if (!raw) continue;
    const m = /^([A-Za-z][A-Za-z0-9]*?)(?:\s*[*x×]\s*(\d+))?$/.exec(raw);
    const name = m ? m[1] : raw;
    const times = m && m[2] ? Math.max(1, parseInt(m[2], 10)) : 1;
    const key = [...known].find((k) => k.toLowerCase() === name.toLowerCase());
    if (!key) {
      // A run of single-letter codes ("HPHHPP") is one bead per letter.
      const letters = name.toUpperCase().split('');
      if (name.length > 1 && letters.every((c) => LETTER_CODES.has(c) && known.has(c))) {
        for (let i = 0; i < times; i++) tokens.push(...letters);
        continue;
      }
      return { tokens: [], error: name };
    }
    for (let i = 0; i < times; i++) tokens.push(key);
  }
  // "CH3 C CH3": a bare C among united-atom groups is the quaternary carbon (CQ).
  if (tokens.includes('C') && tokens.some((t) => t !== 'C' && isUnitedAtom(t))) {
    for (let i = 0; i < tokens.length; i++) if (tokens[i] === 'C') tokens[i] = 'CQ';
  }
  return { tokens };
};

/** True when every token is a united-atom group (real units, Å bond lengths). */
export const isUnitedAtomSequence = (tokens: readonly string[]): boolean =>
  tokens.length > 0 && tokens.every((t) => isUnitedAtom(t));

export interface BeadChainTemplate {
  molecule: Molecule;
  chainHeadAtomIndex: number;
  chainTailAtomIndex: number;
}

/**
 * One chain of beads as a self-avoiding random walk: successive bonds turn by
 * at most ~70°, and a new bead is placed at least 0.9 bond lengths from every
 * bead it is not bonded to (60 tries, then it continues straight). A single
 * token is repeated `chainLength` times; a longer sequence is used as given.
 * The chain is centered on the origin; head is bead 0, tail the last bead.
 */
export const buildBeadChainTemplate = (opts: {
  tokens: readonly string[];
  chainLength: number;
  bondLengthAng: number;
  name?: string;
  types?: BeadType[];
  random?: () => number;
}): BeadChainTemplate => {
  const rnd = opts.random ?? Math.random;
  const seq =
    opts.tokens.length === 1 ? Array(Math.max(1, Math.round(opts.chainLength))).fill(opts.tokens[0]) : [...opts.tokens];
  if (seq.length === 0) throw new Error('beadModel: an empty bead sequence');
  const b = opts.bondLengthAng;
  const minSep = 0.9 * b;
  const pts: Vector3[] = [new Vector3()];
  const randomUnit = (): Vector3 => {
    const z = 2 * rnd() - 1;
    const phi = 2 * Math.PI * rnd();
    const r = Math.sqrt(Math.max(0, 1 - z * z));
    return new Vector3(r * Math.cos(phi), r * Math.sin(phi), z);
  };
  let prev = randomUnit();
  for (let k = 1; k < seq.length; k++) {
    const last = pts[k - 1];
    let placed: Vector3 | null = null;
    for (let attempt = 0; attempt < 60 && !placed; attempt++) {
      const d = randomUnit();
      if (k > 1 && d.dot(prev) < 0.33) continue; // no sharper than ~70° between bonds
      const cand = last.clone().addScaledVector(d, b);
      let ok = true;
      for (let i = 0; i < k - 1 && ok; i++) if (cand.distanceTo(pts[i]) < minSep) ok = false;
      if (ok) {
        placed = cand;
        prev = d;
      }
    }
    pts.push(placed ?? last.clone().addScaledVector(prev, b));
  }
  const c = new Vector3();
  for (const p of pts) c.add(p);
  c.multiplyScalar(1 / pts.length);
  const beads: BeadSpecBead[] = pts.map((p, i) => ({ type: seq[i], x: p.x - c.x, y: p.y - c.y, z: p.z - c.z }));
  const bonds: [number, number][] = [];
  for (let i = 1; i < seq.length; i++) bonds.push([i - 1, i]);
  const name = opts.name ?? (opts.tokens.length === 1 ? `${opts.tokens[0]}${seq.length}` : seq.join('-'));
  const molecule = buildBeadMolecule({
    name,
    resolution: isUnitedAtomSequence(seq) ? 'united-atom' : 'bead-spring',
    beads,
    bonds,
    types: opts.types,
  });
  molecule.polymerChainHeadAtomIndex = 0;
  molecule.polymerChainTailAtomIndex = seq.length - 1;
  return { molecule, chainHeadAtomIndex: 0, chainTailAtomIndex: seq.length - 1 };
};

/**
 * N copies of a bead template on a face-centered lattice with random
 * orientations (todo #8; the Build Chain dialog has the same recipe inline):
 * copies are named `<name>_<k>`, share the template's bead model and chain
 * indices, and a rod's orientation turns with its copy. Returns the copies
 * and the half width of a cube that holds them with `clearanceAng` to spare.
 */
/**
 * Move newly built molecules along +x until their bounding boxes clear what is
 * already in the lab. Two beads at one point make the pair force NaN, and a near
 * overlap injects heat, so a build that appends must never land on the lab.
 * Returns the largest |coordinate| of the moved atoms, for sizing the container.
 */
export const shiftClearOfOccupied = (
  molecules: readonly Molecule[],
  occupied: readonly { x: number; y: number; z: number }[],
  gapAng = 10,
): number => {
  const atoms = molecules.flatMap((m) => m.atoms);
  if (occupied.length > 0 && atoms.length > 0) {
    const occupiedMax = Math.max(...occupied.map((p) => p.x));
    const newMin = Math.min(...atoms.map((a) => a.position.x));
    const dx = occupiedMax + gapAng - newMin;
    if (dx > 0)
      for (const a of atoms) {
        a.position.x += dx;
        if (a.initialPosition) a.initialPosition.x += dx;
      }
  }
  let maxAbs = 0;
  for (const a of atoms)
    maxAbs = Math.max(maxAbs, Math.abs(a.position.x), Math.abs(a.position.y), Math.abs(a.position.z));
  return maxAbs;
};

export const placeBeadCopies = (
  template: Molecule,
  count: number,
  clearanceAng = 5,
  random: () => number = Math.random,
): { molecules: Molecule[]; halfWidth: number } => {
  let maxR2 = 0;
  let reach = 0;
  for (const a of template.atoms) {
    maxR2 = Math.max(maxR2, a.position.lengthSq());
    if (a.bead) reach = Math.max(reach, ((a.bead.aspect ?? 1) * a.bead.sigma) / 2);
  }
  const extent = Math.sqrt(maxR2) + reach;
  const n = Math.max(1, Math.round(count));
  if (n === 1) {
    const one = Molecule.clone(template, true);
    one.name = template.name;
    one.beads = template.beads;
    return { molecules: [one], halfWidth: extent + clearanceAng };
  }
  const nn = 2 * extent + clearanceAng;
  const aLat = nn * Math.SQRT2;
  const half = aLat / 2;
  const r = Math.max(1, Math.ceil(Math.cbrt(n) / 2) + 1);
  const sites: Vector3[] = [];
  for (let i = -r; i <= r; i++)
    for (let j = -r; j <= r; j++)
      for (let k = -r; k <= r; k++) {
        const bx = i * aLat;
        const by = j * aLat;
        const bz = k * aLat;
        sites.push(
          new Vector3(bx, by, bz),
          new Vector3(bx + half, by + half, bz),
          new Vector3(bx + half, by, bz + half),
          new Vector3(bx, by + half, bz + half),
        );
      }
  sites.sort((p, q) => p.lengthSq() - q.lengthSq());
  const positions = sites.slice(0, n);
  const center = new Vector3();
  for (const p of positions) center.add(p);
  center.multiplyScalar(1 / n);
  let maxOffset = 0;
  for (const p of positions) {
    p.sub(center);
    maxOffset = Math.max(maxOffset, p.length());
  }
  const molecules: Molecule[] = [];
  for (let k = 0; k < n; k++) {
    const copy = Molecule.clone(template, true);
    copy.name = `${template.name}_${k + 1}`;
    copy.beads = template.beads;
    copy.polymerChainHeadAtomIndex = template.polymerChainHeadAtomIndex;
    copy.polymerChainTailAtomIndex = template.polymerChainTailAtomIndex;
    const axis = new Vector3(random() - 0.5, random() - 0.5, random() - 0.5).normalize();
    const q = new Quaternion().setFromAxisAngle(axis, random() * Math.PI * 2);
    const offset = positions[k];
    for (const a of copy.atoms) {
      a.position.applyQuaternion(q).add(offset);
      a.initialPosition?.applyQuaternion(q).add(offset);
      a.orientation?.applyQuaternion(q);
      a.initialOrientation?.applyQuaternion(q);
    }
    molecules.push(copy);
  }
  return { molecules, halfWidth: maxOffset + extent + clearanceAng };
};

/**
 * `chainCount` chains packed into one cube at the reduced number density ρ*
 * (beads σ³ / V): each chain is a self-avoiding random walk that also avoids
 * every bead already placed (0.9 bond lengths), restarted when it runs out of
 * room. The start of a melt or a lipid solution, where the copies-on-a-lattice
 * placement (placeBeadCopies) would be far too dilute. Returns the chains,
 * named `<name>_<k>`, and the cube edge.
 */
export const buildPackedBeadChains = (opts: {
  tokens: readonly string[];
  chainLength: number;
  chainCount: number;
  bondLengthAng: number;
  densityReduced: number;
  name?: string;
  random?: () => number;
}): { molecules: Molecule[]; edgeAng: number } => {
  const rnd = opts.random ?? Math.random;
  const seq =
    opts.tokens.length === 1 ? Array(Math.max(1, Math.round(opts.chainLength))).fill(opts.tokens[0]) : [...opts.tokens];
  const nBeads = seq.length * opts.chainCount;
  const sigma = BY_NAME.get(seq[0])?.sigma ?? DEFAULT_BEAD_SCALE.sigma;
  const edge = Math.cbrt((nBeads * sigma * sigma * sigma) / opts.densityReduced);
  const half = edge / 2 - sigma / 2;
  const b = opts.bondLengthAng;
  const minSep = 0.9 * b;
  const placed: Vector3[] = [];
  const randomUnit = (): Vector3 => {
    const z = 2 * rnd() - 1;
    const phi = 2 * Math.PI * rnd();
    const r = Math.sqrt(Math.max(0, 1 - z * z));
    return new Vector3(r * Math.cos(phi), r * Math.sin(phi), z);
  };
  const inside = (p: Vector3) => Math.abs(p.x) <= half && Math.abs(p.y) <= half && Math.abs(p.z) <= half;
  const clear = (p: Vector3, own: Vector3[], skipLast: number) => {
    for (const q of placed) if (p.distanceTo(q) < minSep) return false;
    for (let i = 0; i < own.length - skipLast; i++) if (p.distanceTo(own[i]) < minSep) return false;
    return true;
  };
  const chains: Vector3[][] = [];
  for (let c = 0; c < opts.chainCount; c++) {
    let done: Vector3[] | null = null;
    for (let restart = 0; restart < 400 && !done; restart++) {
      const own: Vector3[] = [];
      const start = new Vector3((2 * rnd() - 1) * half, (2 * rnd() - 1) * half, (2 * rnd() - 1) * half);
      if (!clear(start, own, 0)) continue;
      own.push(start);
      let prev = randomUnit();
      let ok = true;
      for (let k = 1; k < seq.length && ok; k++) {
        let next: Vector3 | null = null;
        for (let attempt = 0; attempt < 120 && !next; attempt++) {
          const d = randomUnit();
          // Packed chains may turn sharply (only a fold straight back is refused), so they fit a dense box.
          if (k > 1 && d.dot(prev) < -0.3) continue;
          const cand = own[k - 1].clone().addScaledVector(d, b);
          if (!inside(cand) || !clear(cand, own, 1)) continue;
          next = cand;
          prev = d;
        }
        if (!next) ok = false;
        else own.push(next);
      }
      if (ok) done = own;
    }
    if (!done)
      throw new Error(
        `beadModel: could not pack ${opts.chainCount} chains at ρ* = ${opts.densityReduced}; lower the density`,
      );
    chains.push(done);
    placed.push(...done);
  }
  const base = opts.name ?? (opts.tokens.length === 1 ? `${opts.tokens[0]}${seq.length}` : seq.join('-'));
  const molecules = chains.map((pts, k) => {
    const beads: BeadSpecBead[] = pts.map((p, i) => ({ type: seq[i], x: p.x, y: p.y, z: p.z }));
    const bonds: [number, number][] = [];
    for (let i = 1; i < seq.length; i++) bonds.push([i - 1, i]);
    const m = buildBeadMolecule({
      name: opts.chainCount > 1 ? `${base}_${k + 1}` : base,
      resolution: isUnitedAtomSequence(seq) ? 'united-atom' : 'bead-spring',
      beads,
      bonds,
    });
    m.polymerChainHeadAtomIndex = 0;
    m.polymerChainTailAtomIndex = seq.length - 1;
    return m;
  });
  return { molecules, edgeAng: edge };
};

const ROD_MIX_COLORS = ['#d98c3a', '#3a9ad9', '#5fb35a', '#c75fa8', '#8a6fd1', '#d9c23a'];

/**
 * A rod type of a given size. Its mass follows its volume from 5CB (18 × 5 Å,
 * 249.4 g/mol), its well is 5CB's unless given. 18 × 5 Å with no well given is
 * the built-in ROD itself.
 */
export const rodTypeOfSize = (lengthAng: number, widthAng: number, epsilonOverK?: number, index = 0): BeadType => {
  if (!(widthAng > 0) || !(lengthAng > widthAng)) throw new Error('beadModel: a rod must be longer than it is wide');
  const rod = BY_NAME.get('ROD')!;
  const refLength = rod.sigma * rod.aspect!;
  if (Math.abs(lengthAng - refLength) < 1e-9 && widthAng === rod.sigma && epsilonOverK === undefined) return rod;
  return generic(
    `ROD${lengthAng}x${widthAng}`,
    `rod ${lengthAng} × ${widthAng} Å (Gay-Berne, aspect ${(lengthAng / widthAng).toFixed(2)})`,
    ROD_MIX_COLORS[index % ROD_MIX_COLORS.length],
    (rod.mass * lengthAng * widthAng * widthAng) / (refLength * rod.sigma * rod.sigma),
    widthAng,
    epsilonOverK ?? 540,
    { aspect: lengthAng / widthAng, suggestedK: rod.suggestedK },
  );
};

/**
 * Lay an aligned rod cell (buildRodBoxTemplate, buildDopedRodCell) along x instead
 * of z, in place: x and z swap in every position and orientation and in the cell,
 * so the director lies across an aligning field along z (a display pixel's off
 * state). A reflection, which a cell of non-chiral rods cannot tell apart.
 */
export const layRodCellAlongX = (cell: {
  molecule: Molecule;
  boxAng: { lx: number; ly: number; lz: number };
}): void => {
  for (const a of cell.molecule.atoms) {
    a.position.set(a.position.z, a.position.y, a.position.x);
    for (const v of [a.orientation, a.initialOrientation]) if (v) v.set(v.z, v.y, v.x);
  }
  const { lx, lz } = cell.boxAng;
  cell.boxAng.lx = lz;
  cell.boxAng.lz = lx;
};

/**
 * A dichroic dye guest of a given size (DYE{l}x{d}, deep blue): a rod like
 * rodTypeOfSize's, flagged as a dye so the lab reports its absorption of polarized
 * light, and coupled to the aligning field by fieldCoupling (default 0: the field
 * acts on the host alone, as for a dye of small dielectric anisotropy, so the dye
 * turns only because the host turns it).
 */
export const dyeRodType = (lengthAng: number, widthAng: number, fieldCoupling = 0, epsilonOverK?: number): BeadType => {
  const rod = rodTypeOfSize(lengthAng, widthAng, epsilonOverK ?? 540, 1);
  return {
    ...rod,
    name: `DYE${lengthAng}x${widthAng}`,
    label: `dichroic dye rod ${lengthAng} × ${widthAng} Å (Gay-Berne, aspect ${(lengthAng / widthAng).toFixed(2)})`,
    color: '#2f4fd6',
    dye: true,
    fieldCoupling,
  };
};

/**
 * A gas of rods (todo #4): N Gay-Berne rods at random positions AND random
 * orientations in a cube at the reduced number density ρ* (default 0.1), placed
 * one by one and rejected when the Gay-Berne pair energy with any rod already
 * placed exceeds ε₀ (an overlap). If the cube cannot take them the edge grows by
 * 10 % and the placement starts over, so the achieved density is reported.
 * The copies-on-a-lattice placement gave rods a box a hundred times too large
 * (ρ* ≈ 0.004), in which they never met.
 *
 * `species` makes a mixture of rods of different lengths and widths, longest
 * first because they are the hardest to place; ρ* is then N⟨σ₀³⟩/V. `aligned`
 * starts each rod along ±z in place of a random direction.
 */
export const buildRodGasTemplate = (opts: {
  count: number;
  densityReduced?: number;
  type?: string;
  species?: { type: BeadType; count: number }[];
  aligned?: boolean;
  random?: () => number;
}): BeadChainTemplate & { edgeAng: number; achievedDensity: number; packingFraction: number } => {
  const rnd = opts.random ?? Math.random;
  let species = opts.species;
  if (!species?.length) {
    const t = BY_NAME.get(opts.type ?? 'ROD');
    species = t ? [{ type: t, count: Math.max(1, opts.count) }] : [];
  }
  species = species
    .map((s) => ({ type: s.type, count: Math.max(0, Math.round(s.count)) }))
    .filter((s) => s.count > 0)
    .sort((x, y) => y.type.sigma * (y.type.aspect ?? 1) - x.type.sigma * (x.type.aspect ?? 1));
  if (species.length === 0 || species.some((s) => (s.type.aspect ?? 1) <= 1))
    throw new Error('beadModel: buildRodGasTemplate needs a rod type');
  const rodTypes = species.flatMap((s) => new Array<BeadType>(s.count).fill(s.type));
  const n = rodTypes.length;
  const maxLength = Math.max(...species.map((s) => s.type.sigma * s.type.aspect!));
  const maxWidth = Math.max(...species.map((s) => s.type.sigma));
  const sumCubed = rodTypes.reduce((sum, t) => sum + t.sigma * t.sigma * t.sigma, 0);
  const sumVolume = rodTypes.reduce((sum, t) => sum + (Math.PI / 6) * t.aspect! * t.sigma * t.sigma * t.sigma, 0);
  const out = newGbPairResult();
  const rij = new Vector3();
  const reach2 = Math.pow(maxLength + 2.5 * maxWidth, 2);
  const randomUnit = (): Vector3 => {
    const z = 2 * rnd() - 1;
    const phi = 2 * Math.PI * rnd();
    const r = Math.sqrt(Math.max(0, 1 - z * z));
    return new Vector3(r * Math.cos(phi), r * Math.sin(phi), z);
  };
  let edge = Math.cbrt(sumCubed / (opts.densityReduced ?? 0.1));
  for (let grow = 0; grow < 12; grow++) {
    const half = Math.max(0, edge / 2 - maxLength / 2);
    const pos: Vector3[] = [];
    const ors: Vector3[] = [];
    let ok = true;
    for (let k = 0; k < n && ok; k++) {
      let placed = false;
      for (let attempt = 0; attempt < 300 && !placed; attempt++) {
        const c = new Vector3((2 * rnd() - 1) * half, (2 * rnd() - 1) * half, (2 * rnd() - 1) * half);
        const u = opts.aligned ? new Vector3(0, 0, rnd() < 0.5 ? 1 : -1) : randomUnit();
        const ti = rodTypes[k];
        let overlap = false;
        for (let j = 0; j < pos.length && !overlap; j++) {
          rij.subVectors(c, pos[j]);
          if (rij.lengthSq() > reach2) continue;
          const tj = rodTypes[j];
          const p = gbUnlikeParams(ti.sigma, ti.epsilon, ti.aspect!, tj.sigma, tj.epsilon, tj.aspect!);
          if (gbPair(rij, u, ors[j], p, out) && out.energy > p.epsilon0) overlap = true;
        }
        if (!overlap) {
          pos.push(c);
          ors.push(u);
          placed = true;
        }
      }
      if (!placed) ok = false;
    }
    if (ok) {
      const molecule = buildBeadMolecule({
        name: species.length === 1 ? `${species[0].type.name}${n}` : `RODMIX${n}`,
        resolution: 'ellipsoid',
        beads: pos.map((c, i) => ({ type: rodTypes[i].name, x: c.x, y: c.y, z: c.z })),
        types: species.map((s) => s.type),
      });
      molecule.atoms.forEach((a, i) => {
        a.orientation?.copy(ors[i]);
        a.initialOrientation?.copy(ors[i]);
      });
      return {
        molecule,
        chainHeadAtomIndex: 0,
        chainTailAtomIndex: n - 1,
        edgeAng: edge,
        achievedDensity: sumCubed / (edge * edge * edge),
        packingFraction: sumVolume / (edge * edge * edge),
      };
    }
    edge *= 1.1;
  }
  throw new Error(`beadModel: could not place ${n} rods at ρ* = ${opts.densityReduced ?? 0.1}; lower the density`);
};
